import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Gift, Smartphone, Sparkles, Star } from 'lucide-react';

/**
 * PATH B ONLY — the post-reveal 2X offer. Rendered exclusively when the
 * resolved setup has Lead Generation ON; Path A (Lead Gen OFF) never mounts it
 * and keeps the untouched normal accept flow.
 *
 * Everything here is a real component: the gift artwork is whatever the guest
 * actually revealed (doubled artwork if the setup has it), and the branding is
 * the org's own logo over its own wallpaper — no baked-in background image and
 * nothing hardcoded to one brand.
 */
const CONFETTI = [
    ['6%', '8%', -18, '#ffc72c'], ['14%', '22%', 32, '#d0021b'],
    ['88%', '10%', 24, '#ffc72c'], ['80%', '26%', -40, '#d0021b'],
    ['4%', '46%', 12, '#d0021b'], ['93%', '52%', -22, '#ffc72c'],
    ['10%', '68%', 44, '#ffc72c'], ['86%', '72%', -12, '#d0021b'],
];

export const LeadGenOfferScreen = ({
    giftImageUrl, prizeLabel, onAccept, onDecline, onImageError,
}) => {
    // The pulse stops the moment the guest commits, so the press animation
    // reads cleanly instead of fighting an expanding ring.
    const [committed, setCommitted] = useState(false);
    const { t } = useTranslation();
    const shown = useRef(false);

    useEffect(() => {
        if (shown.current) return;   // one impression per mount
        shown.current = true;
    }, []);

    return (
        <div className="relative w-full max-w-md mx-auto px-1 pt-2 pb-1 animate-in fade-in zoom-in-95 duration-400"
             data-testid="gg-leadgen">
            {/* celebratory burst behind everything */}
            <div aria-hidden className="pointer-events-none absolute inset-0 -z-10"
                 style={{ background: 'radial-gradient(ellipse at 50% 26%, rgba(255,199,44,0.30) 0%, rgba(208,2,27,0.16) 42%, transparent 72%)' }} />
            {CONFETTI.map(([left, top, rot, color], i) => (
                <span key={i} aria-hidden
                      className="pointer-events-none absolute -z-10 rounded-[1px] opacity-80"
                      style={{ left, top, width: 7, height: 20, background: color,
                               transform: `rotate(${rot}deg)`,
                               animation: `ggFloat 2.6s ease-in-out ${i * 0.18}s infinite alternate` }} />
            ))}
            <style>{`@keyframes ggFloat{from{transform:translateY(0) rotate(0)}to{transform:translateY(-10px) rotate(14deg)}}`}</style>

            {/* NO logo here — the merchant logo is already on the page above
                this screen (gg-merchant-logo), and showing it twice read as a
                duplicate. */}

            {/* HEADLINE — 2X is the loudest thing on the screen */}
            <div className="text-center mt-2">
                <p className="text-[15px] font-black uppercase tracking-[0.12em] text-white leading-tight"
                   style={{ textShadow: '0 2px 6px rgba(0,0,0,0.65)' }}
                   data-testid="gg-leadgen-headline">
                    {t('leadGeneration.offerLine1')}
                </p>
                <div className="relative block mt-0.5">
                    <span aria-hidden className="absolute inset-0 -m-6 -z-10"
                          style={{ background: 'conic-gradient(from 0deg, rgba(255,199,44,0.55) 0 6deg, transparent 6deg 30deg)' }} />
                    <span className="block text-[6.5rem] leading-[0.8] font-black italic tracking-tighter"
                          style={{ backgroundImage: 'linear-gradient(180deg,#fff6cf 6%,#ffc72c 42%,#e8a000 74%,#b76c00 100%)',
                                   WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                                   filter: 'drop-shadow(0 4px 0 rgba(120,60,0,0.55)) drop-shadow(0 8px 16px rgba(0,0,0,0.5))' }}
                          data-testid="gg-leadgen-2x">
                        2X
                    </span>
                </div>
                <div className="-mt-1.5">
                    <p className="inline-block px-3.5 py-1 rounded-md bg-[#d0021b] text-white text-lg font-black uppercase tracking-wide shadow-lg border-2 border-white/25"
                       data-testid="gg-leadgen-your-gift">
                        {t('leadGeneration.offerYourGift')}
                    </p>
                </div>
            </div>

            {/* THE GIFT THEY ACTUALLY WON — never hardcoded. The artwork fills
                the card nearly edge to edge: the previous fixed 144px-tall
                object-contain frame turned wide 3:2 artwork into a strip
                floating in white. Its 3:2 box matches the artwork (800x533), so object-cover crops nothing, and both buttons stay on
                screen at 390x844 without scrolling. */}
            <div className="mt-4 overflow-hidden rounded-2xl border-2 border-amber-300/80 bg-white/95 p-1 pb-0 shadow-[0_0_28px_rgba(255,199,44,0.45)]"
                 data-testid="gg-leadgen-gift-card">
                {giftImageUrl ? (
                    <img src={giftImageUrl} alt="" data-testid="gg-leadgen-image"
                         onError={onImageError}
                         className="block w-full aspect-[3/2] max-h-[34vh] object-cover rounded-xl"
                         decoding="async" />
                ) : (
                    <div className="w-full aspect-[3/2] max-h-[34vh] flex flex-col items-center justify-center gap-1.5 rounded-xl bg-slate-100 text-slate-400"
                         data-testid="gg-leadgen-image-placeholder">
                        <Gift className="w-14 h-14" />
                    </div>
                )}
                {/* Explicit colour, not just a utility class: this sits on the
                    white card inside a screen whose every other element is
                    white text, and it MUST never inherit that. */}
                <p className="text-center text-lg font-black py-1.5 px-2 leading-tight"
                   style={{ color: '#0f172a', WebkitTextFillColor: '#0f172a' }}
                   data-testid="gg-leadgen-prize">
                    {prizeLabel}
                </p>
            </div>

            {/* app benefits */}
            <div className="mt-3 grid grid-cols-3 gap-1 text-center" data-testid="gg-leadgen-benefits">
                {[[Smartphone, 'benefit1'], [Gift, 'benefit2'], [Star, 'benefit3']].map(([Icon, key]) => (
                    <div key={key} className="flex flex-col items-center gap-1 px-0.5 self-start">
                        <Icon className="w-4 h-4 text-[#ffc72c] shrink-0" />
                        <span className="text-[9px] font-bold uppercase leading-[1.15] text-white/90 tracking-wide">
                            {t(`leadGeneration.${key}`)}
                        </span>
                    </div>
                ))}
            </div>

            {/* GREEN = download & double. BLACK = original gift. No third option. */}
            <div className="mt-4 space-y-2.5">
                {/* The pulse lives on a WRAPPER, never on the button: the raised 3D
                    edge IS a box-shadow, and `status-pulse` animates box-shadow, so
                    putting it on the button would wipe that edge. Same radius so the
                    ring hugs the button, and [transform:translateZ(0)] for the same
                    reason as Loyal Review's status box — it keeps the animated
                    box-shadow on its own layer and stops Safari ghosting.
                    `motion-reduce:animate-none` honours prefers-reduced-motion. */}
                <div className={`rounded-2xl [transform:translateZ(0)] motion-reduce:animate-none ${committed ? '' : 'animate-status-pulse'}`}
                     data-testid="gg-leadgen-continue-pulse">
                <button type="button" onClick={onAccept} onPointerDown={() => setCommitted(true)}
                        data-testid="gg-leadgen-continue"
                        className="w-full min-h-[66px] rounded-2xl px-4 flex items-center justify-between gap-3
                                   bg-gradient-to-b from-[#1fa845] to-[#10742f] border-2 border-emerald-300/70
                                   shadow-[0_6px_0_#0a5322,0_10px_22px_rgba(0,0,0,0.45)]
                                   active:translate-y-[3px] active:shadow-[0_3px_0_#0a5322]
                                   transition-transform duration-100">
                    <span className="flex-1 text-center">
                        <span className="block text-xl font-black text-white leading-tight">
                            {t('leadGeneration.downloadApp')}
                        </span>
                        <span className="block text-[13px] font-black uppercase tracking-wide text-[#ffc72c]">
                            {t('leadGeneration.get2xYourGift')}
                        </span>
                    </span>
                    <ChevronRight className="w-6 h-6 text-white shrink-0" />
                </button>
                </div>

                <button type="button" onClick={onDecline} data-testid="gg-leadgen-decline"
                        className="w-full min-h-[58px] rounded-2xl px-4 flex items-center justify-between gap-3
                                   bg-gradient-to-b from-[#2b2b2b] to-[#0a0a0a] border-2 border-white/25
                                   shadow-[0_5px_0_#000,0_8px_18px_rgba(0,0,0,0.4)]
                                   active:translate-y-[3px] active:shadow-[0_2px_0_#000]
                                   transition-transform duration-100">
                    <span className="flex-1 text-center text-base font-bold text-white leading-tight">
                        {t('leadGeneration.declineOriginal')}
                    </span>
                    <ChevronRight className="w-5 h-5 text-white/70 shrink-0" />
                </button>
            </div>

            <p className="mt-2 text-center text-[10px] text-white/55 flex items-center justify-center gap-1">
                <Sparkles className="w-3 h-3" />{t('leadGeneration.offerFootnote')}
            </p>
        </div>
    );
};
